import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { type ColorTokens, type GetProps, getTokenValue, styled, View } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import type { Status } from "../shared-types/status";
import { type PillSize, pillSizes } from "../themes/token-pill";
import { pillTextTruncation, StyledPill, statusColorTokens } from "./StyledPill";
import { Interface2, Interface3 } from "./Typography";
export type BadgeSize = "small" | "medium";

// Each Badge size's row on the pill scale.
const badgePillSize = {
  small: "$si2.5",
  medium: "$si3"
} as const satisfies Record<BadgeSize, PillSize>;

// Geometry is Badge policy — StyledPill carries only the skin.
const BadgeFrame = styled(StyledPill, {
  name: "Badge",
  variants: {
    size: {
      medium: {
        height: badgePillSize.medium,
        paddingHorizontal: pillSizes[badgePillSize.medium].sidePadding
      },
      small: {
        height: badgePillSize.small,
        paddingHorizontal: pillSizes[badgePillSize.small].sidePadding
      }
    },
    status: {
      brand: {
        backgroundColor: "$bgBrandIntense",
        borderColor: "$bgBrandIntense"
      }
    },
    bordered: {
      true: {},
      false: {
        borderWidth: 0
      }
    }
  } as const,
  defaultVariants: {
    size: "medium"
  }
});

// `interactive`/`disabled` are StyledPill variants Badge doesn't support — a
// static `role="status"` pill has no interactive states to toggle.
export type BadgeProps = Omit<GetProps<typeof BadgeFrame>, "children" | "interactive" | "disabled" | "status"> & {
  status?: Status | "brand";
  size?: BadgeSize;
  bordered?: boolean;
  /** Override the status foreground; pair with `backgroundColor`. */
  iconColor?: ColorTokens;
  textColor?: ColorTokens;
  iconLeft?: FunctionComponent<IconProps>;
  iconRight?: FunctionComponent<IconProps>;
  children: ReactNode;
};
const brandColorTokens = {
  text: "$textOnBrand",
  icon: "$iconOnBrand"
} as const;
const BadgeTextMedium = styled(Interface2, pillTextTruncation);
const BadgeTextSmall = styled(Interface3, pillTextTruncation);
export const Badge = (props: BadgeProps) => {
  const {
    status = "neutral",
    size = "medium",
    iconLeft: IconLeft,
    iconRight: IconRight,
    iconColor,
    textColor,
    children,
    ...rest
  } = props;
  const {
    iconSize: iconSizeToken,
    iconSidePadding
  } = pillSizes[badgePillSize[size]];
  const iconSize = useMemo(() => Number(getTokenValue(iconSizeToken, "size")), [iconSizeToken]);
  const statusTokens = status === "brand" ? brandColorTokens : statusColorTokens[status];
  const tokens = {
    icon: iconColor ?? statusTokens.icon,
    text: textColor ?? statusTokens.text
  };
  const BadgeText = size === "small" ? BadgeTextSmall : BadgeTextMedium;
  return <BadgeFrame
  // Variants first, so a caller's colours and padding win; the icon side
  // keeps its own padding.
  status={status} size={size} {...rest} render="span" role="status" {...IconLeft ? {
    paddingLeft: iconSidePadding
  } : {}} {...IconRight ? {
    paddingRight: iconSidePadding
  } : {}} data-sentry-element="BadgeFrame" data-sentry-component="Badge" data-sentry-source-file="Badge.tsx">
      {IconLeft ? <View flexShrink={0}>
          <IconLeft size={iconSize} color={tokens.icon} />
        </View> : null}
      <BadgeText color={tokens.text} flexShrink={1} numberOfLines={1} data-sentry-element="BadgeText" data-sentry-source-file="Badge.tsx">
        {children}
      </BadgeText>
      {IconRight ? <View flexShrink={0}>
          <IconRight size={iconSize} color={tokens.icon} />
        </View> : null}
    </BadgeFrame>;
};